import React from 'react';
import {
  LTBox,
  LTText,
  LTCopyToClipboard,
  LTIconButton
} from '@lambdatestincprivate/lt-components';
import styles from './index.module.css';

const lineToPlainText = (line) => line.map((token) => token.text).join('');

/**
 * @param {string} heading
 * @param {React.ReactNode} subHeading
 * @param {Array<Array<{ text: string, color?: string }>>} codeLines
 * @param {string} copyAriaLabel
 * @param {string} copiedAriaLabel
 * @param {React.ReactNode} children - Custom body content (e.g. form controls).
 * @param {React.ReactNode} footerText - Optional footerText content (left aligned).
 * @param {React.ReactNode} footerCta - Optional footerCta button (left aligned).
 * @param {string} dataTestId - Test id applied to the card.
 */

const OnboardingStepsWrapper = ({
  heading,
  subHeading,
  codeLines,
  copyAriaLabel,
  copiedAriaLabel,
  children,
  footerText,
  footerCta,
  dataTestId
}) => {
  const hasCodeBlock = Array.isArray(codeLines) && codeLines.length > 0;
  const textToCopy = hasCodeBlock
    ? codeLines.map(lineToPlainText).join('\n')
    : '';

  return (
    <LTBox
      as="section"
      className={`${styles['display-flex-column']} ${styles['step-card']}`}
      data-testid={dataTestId}
    >
      {(heading || subHeading) && (
        <LTBox
          className={styles['display-flex-column']}
          style={{ gap: '2px' }}
        >
          {heading && (
            <LTText
              as="h2"
              variant={LTText.TextVariant.SUBHEADER_BOLD}
              style={{ margin: 0 }}
            >
              {heading}
            </LTText>
          )}
          {subHeading && (
            <LTText
              as="p"
              variant={LTText.TextVariant.SMALL_REGULAR}
              color="#636c76"
              style={{ margin: 0 }}
            >
              {subHeading}
            </LTText>
          )}
        </LTBox>
      )}

      {children}

      {hasCodeBlock && (
        <LTBox className={styles['code-block']}>
          <LTBox className={styles['display-flex-column']}>
            {codeLines.map((_line, lineIndex) => (
              <span key={`prompt-${lineIndex}`} className={styles['code-prompt']}>
                $
              </span>
            ))}
          </LTBox>
          <LTBox as="code" className={`${styles['code-mono']}`}>
            {codeLines.map((line, lineIndex) => (
              <React.Fragment key={`line-${lineIndex}`}>
                {lineIndex > 0 && <br />}
                {line.map((token, tokenIndex) => (
                  <span
                    key={`token-${lineIndex}-${tokenIndex}`}
                    style={token.color ? { color: token.color } : undefined}
                  >
                    {token.text}
                  </span>
                ))}
              </React.Fragment>
            ))}
          </LTBox>
          <LTCopyToClipboard
            buttonVariant={LTIconButton.IconButtonVariants.INVISIBLE}
            copyAriaLabel={copyAriaLabel}
            copiedAriaLabel={copiedAriaLabel}
            textToCopy={textToCopy}
            useNavigator
          />
        </LTBox>
      )}
      {footerText && (
        <LTText variant={LTText.TextVariant.SMALL_REGULAR} color="#636c76">
          {footerText}
        </LTText>
      )}
      {footerCta && (
        <LTBox
          className={styles['display-flex-column']}
          style={{ alignItems: 'flex-start' }}
        >
          {footerCta}
        </LTBox>
      )}
    </LTBox>
  );
};

export default React.memo(OnboardingStepsWrapper);
